import { useState, useEffect } from 'react';
import { RefreshCw, Wifi, WifiOff, AlertTriangle } from 'lucide-react';

// Import components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import DashboardPage from './DashboardPage';
import ExpedieriPage from './ExpedieriPage';
import ExpedieriFinalizatePage from './ExpedieriFinalizatePage';
import SoferiPage from './SoferiPage';
import RapoarteOperationalePage from './RapoarteOperationalePage';
import MobileWarning from './components/MobileWarning';
import ExpedieriActive from './ExpedieriActive';
import { getAuthHeaders } from '@/utils/getauthheaders';
import Clients from '../admin/components/Clients';

const DispacerDashboard = () => {
  // Essential state management
  const [currentTime, setCurrentTime] = useState(new Date());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false); // Added missing state
  // Simplified API state
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastRefresh, setLastRefresh] = useState(null); // Added missing state
  // API configuration
  const API_BASE_URL = `${import.meta.env.VITE_BACKEND_URL}/api`;

  // Initialize token
  useEffect(() => {
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    setToken(storedToken);
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Time update
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  // Simplified refresh function
  const refreshData = async () => {
    if (!token) {
      setError('Necesită autentificare');
      return;
    }

    setLoading(true);
    setConnectionStatus('connecting');
    
    try {
      await fetchShipments();
      setConnectionStatus('connected');
      setError(null);
    } catch (err) {
      setConnectionStatus('error');
      setError('Eroare la încărcare');
    } finally {
      setLoading(false);
    }
  };

  // Simplified shipments fetching
  const fetchShipments = async () => {
    const response = await fetch(`${API_BASE_URL}/shipments?format=detailed&limit=50`, {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`Eroare ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && Array.isArray(data.data)) {
      // Transform to essential data only
      const essentialShipments = data.data.map(item => ({
        id: item.id,
        client: item.client_nume || 'Client necunoscut',
        destinatie: item.destinatie || 'Destinație necunoscută',
        sofer: item.sofer_nume || null,
        vehicul: item.vehicul_numar || null,
        status: item.status || 'in_asteptare',
        prioritate: item.prioritate || 'normala',
        progres: parseInt(item.progres?.replace('%', '') || 0),
        oraPlecarii: extractTime(item.data_transport),
        telefon: item.client?.telefon,
        // Remove repetitive/unnecessary fields
      }));
      
      setShipments(essentialShipments);
    }
  };

  // Utility function
  const extractTime = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleTimeString('ro-RO', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return null;
    }
  };

  // Simplified status update
  const updateShipmentStatus = async (shipmentId, newStatus) => {
    if (!token) return { success: false, error: 'Token lipsă' };

    try {
      const response = await fetch(`${API_BASE_URL}/shipments/${shipmentId}/status`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Eroare actualizare');

      const result = await response.json();
      
      if (result.success) {
        setShipments(prev => 
          prev.map(shipment => 
            shipment.id === shipmentId 
              ? { ...shipment, status: newStatus }
              : shipment
          )
        );
        return { success: true };
      }
      throw new Error('Actualizare eșuată');
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Simplified API functions with better error handling
  const getShipmentDetails = async (shipmentId) => {
    if (!token) return { success: false, error: 'Token lipsă' };

    try {
      const response = await fetch(`${API_BASE_URL}/shipments/${shipmentId}/details`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Eroare încărcare detalii');
      
      const data = await response.json();
      return data.success ? { success: true, data: data.data } : { success: false, error: 'Detalii indisponibile' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const fetchDrivers = async () => {
    if (!token) return { success: false, data: [] };

    try {
      const response = await fetch(`${API_BASE_URL}/drivers`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Eroare încărcare șoferi');
      
      const result = await response.json();
      
      if (result.success && Array.isArray(result.data)) {
        // Simplify driver data
        const drivers = result.data.map(driver => ({
          id: driver.id,
          nume: driver.name || 'Necunoscut',
          telefon: driver.telefon,
          vehicul: driver.vehicul_asignat,
          status: driver.is_active ? 'activ' : 'inactiv',
          // Remove excessive fields
        }));
        
        return { success: true, data: drivers };
      }
      return { success: false, data: [] };
    } catch (error) {
      return { success: false, data: [] };
    }
  };

  const fetchVehicles = async () => {
    if (!token) return { success: false, data: [] };

    try {
      const response = await fetch(`${API_BASE_URL}/vehicles`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Eroare încărcare vehicule');
      
      const data = await response.json();
      return { success: true, data: Array.isArray(data) ? data : [] };
    } catch (error) {
      return { success: false, data: [] };
    }
  };

  // Load data on mount
  useEffect(() => {
    if (token) refreshData();
  }, [token]);

  // Auto-refresh only for dashboard - reduced frequency
  useEffect(() => {
    let interval;
    
    if (activeSection === 'dashboard' && token && !error) {
      interval = setInterval(refreshData, 120000); // 2 minutes instead of 30 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeSection, token, error]);

  // Simplified connection icon
  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected': return <Wifi className="h-4 w-4 text-green-500" />;
      case 'connecting': return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      default: return <WifiOff className="h-4 w-4 text-red-500" />;
    }
  };

  // Mobile warning
  if (isMobile) return <MobileWarning />;

  // Simplified props - only essential data
  const pageProps = {
    shipments,
    loading,
    error,
    onRefresh: refreshData,
    onUpdateStatus: updateShipmentStatus,
    onGetDetails: getShipmentDetails,
    onFetchDrivers: fetchDrivers,
    onFetchVehicles: fetchVehicles,
    token
  };

  // Show error state more prominently
  if (error && !token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Eroare de autentificare</h2>
          <p className="text-gray-600">Vă rugăm să vă conectați pentru a accesa dashboard-ul.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar 
        sidebarCollapsed={sidebarCollapsed}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      <div className={`flex flex-col min-h-screen transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        <Header 
        sidebarCollapsed={sidebarCollapsed}
        setSidebarCollapsed={setSidebarCollapsed}
        currentTime={currentTime}
        activeSection={activeSection}
        connectionStatus={connectionStatus}
        lastRefresh={lastRefresh}
        shipments={shipments}
        loading={loading}
        isRefreshing={isRefreshing}
        token={token}
        error={error}
        refreshData={refreshData}
        getConnectionIcon={getConnectionIcon}
      />

        {/* Page Content */}
        <div className="flex-1 p-6">
          {activeSection === 'dashboard' && <DashboardPage {...pageProps} />}
          {activeSection === 'expedieri' && <ExpedieriPage {...pageProps} />}
          {activeSection === 'expedieri-active' && <ExpedieriActive {...pageProps} />}
          {activeSection === 'expedieri-finalizate' && <ExpedieriFinalizatePage {...pageProps} />}
          {activeSection === 'soferi' && <SoferiPage {...pageProps} />}
          {activeSection === 'clienti' && <Clients />}
          {activeSection === 'rapoarte' && <RapoarteOperationalePage {...pageProps} />}
        </div>
      </div>
    </div>
  );
};

export default DispacerDashboard;